import { runStartupSyncOverlay } from './startupSyncOverlay.ts';
import type { ShellAuthApplicationPort } from '../../app-context/domain/ports/ShellAuthApplicationPort.ts';
import type { ShellUiMetadataPort } from '../../app-context/domain/ports/ShellUiMetadataPort.ts';
import type { PresentationShellPort } from '../../presentation/application/ports/PresentationShellPort.ts';
import type { ShellRouteBindingsFactory } from '../domain/ShellRouteBindingsFactory.ts';
import { createFeatureShellPorts, type FeatureShellPorts } from '@src/core/bootstrap/FeatureShellPorts.ts';
import { Router } from '../../routing/infrastructure/drivers/routes/Router.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { ShellUiMetadataFromAppConfig } from './adapters/ShellUiMetadataFromAppConfig.ts';
import type { AppContainer } from '@src/core/bootstrap/AppContainer.ts';
import {
  gateLoginPageOnly,
  gateUnauthenticatedDeepLink,
  ensureLayoutAndMainIfNeeded,
  syncTelemetryUserContext,
} from './applicationBootstrapDomWorkflow.ts';
import {
  installAuthLoginSuccessListener,
  installGlobalErrorTelemetryHandlers,
  installSessionExpiredListener,
  isPublicShellPath,
} from './applicationShellBootstrap.ts';
import { installLocalSqlitePersistFailureListener } from './localSqlitePersistFailureListener.ts';
import { showFatalAppError } from './showFatalAppError.ts';
import { installNativeHardwareBackButtonListener } from '../../routing/infrastructure/nativeHardwareBackButton.ts';
import { notifyPostSnapshotRevisionUpgrade } from './postSnapshotRevisionUpgradeNotify.ts';
import { notifyPostShellBuildRevisionUpgrade } from './postShellBuildRevisionUpgradeNotify.ts';
import {
  getLastStartupCatalogSyncOutcome,
  noteStartupCatalogSyncOutcome,
  type StartupCatalogSyncOutcome,
} from '@src/modules/sync/application/startupCatalogSyncOutcome.ts';
import { consumePostLoginReturnUrl } from '@src/modules/shared/routing/infrastructure/shellPostLoginReturnUrl.ts';
import { retryPostAuthCatalogDownloadIfNeeded } from './postAuthCatalogDownloadRetry.ts';

/** Admin bootstrap: shell UI, routes, and global listeners. */
export class ApplicationBootstrap {
  private readonly router: Router;
  private shellUi!: ShellUiMetadataPort;
  private routerStarted = false;

  /** Constructs this instance. */
  constructor(
    private readonly presentation: PresentationShellPort<FeatureShellPorts>,
    private readonly container: AppContainer,
    private readonly shellRouteBindings: ShellRouteBindingsFactory<FeatureShellPorts>,
  ) {
    this.router = new Router(this.container.getTelemetry(), createCorrelationId);
  }

  /** Run. */
  run(): void {
    this.presentation.prepareShell();

    const auth = this.container.getAuthService();
    const telemetry = this.container.getTelemetry();
    const shellUi = new ShellUiMetadataFromAppConfig();
    this.shellUi = shellUi;

    installGlobalErrorTelemetryHandlers(telemetry, this.container.getNotificationPort());
    installLocalSqlitePersistFailureListener(this.container.getNotificationPort());
    installNativeHardwareBackButtonListener();
    this.mountShellRoutes(auth);
    installSessionExpiredListener(auth, isPublicShellPath);
    installAuthLoginSuccessListener(() => {
      void this.continueAfterLogin();
    });

    this.scheduleDomBootstrap(auth);
  }

  /**
   * If the bundle loads late (network, Docker), `DOMContentLoaded` may have already fired;
   * listening only for the event leaves the shell unmounted (e.g. Playwright E2E).
   */
  private scheduleDomBootstrap(auth: ShellAuthApplicationPort): void {
    const kickoff = () => void this.onDomContentLoaded(auth);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', kickoff);
    } else {
      kickoff();
    }
  }

  /** Returns shell ports. */
  private getShellPorts(auth: ShellAuthApplicationPort): FeatureShellPorts {
    return createFeatureShellPorts({
      container: this.container,
      auth,
      navigation: this.router,
      layout: this.presentation.getLayoutPort(),
      shellUi: this.shellUi,
    });
  }

  /** Mount shell routes. */
  private mountShellRoutes(auth: ShellAuthApplicationPort): void {
    const shellPorts: FeatureShellPorts = this.getShellPorts(auth);
    this.shellRouteBindings(auth, this.router, this.presentation, shellPorts).forEach(({ path, handler }) =>
      this.router.addRoute(path, handler),
    );
  }

  /** Mount layout + start router once (shared by cold boot and in-app login resume). */
  private async finishAuthenticatedShell(
    auth: ShellAuthApplicationPort,
    bootCorrelationId: string,
  ): Promise<void> {
    const telemetry = this.container.getTelemetry();
    const shellPorts = this.getShellPorts(auth);
    const pathAfterAuthGates = globalThis.location.pathname;

    await ensureLayoutAndMainIfNeeded(this.presentation, pathAfterAuthGates, telemetry, shellPorts);

    notifyPostSnapshotRevisionUpgrade(shellPorts);
    notifyPostShellBuildRevisionUpgrade(shellPorts);

    telemetry.logInfo('Bootstrap: router', { correlationId: bootCorrelationId });
    this.startRouterOnce();
  }

  private startRouterOnce(): void {
    if (this.routerStarted) return;
    this.routerStarted = true;
    this.router.start();
  }

  /** Resume shell bootstrap after login without reloading (router was not started on `/login`). */
  private async continueAfterLogin(): Promise<void> {
    const auth = this.container.getAuthService();
    const telemetry = this.container.getTelemetry();
    const bootCorrelationId = createCorrelationId();

    try {
      if (!(await auth.isAuthenticated())) return;

      const postLoginTarget = consumePostLoginReturnUrl('/');
      globalThis.history.replaceState({}, '', postLoginTarget);
      await syncTelemetryUserContext(auth, telemetry, true);

      if (this.routerStarted) {
        await retryPostAuthCatalogDownloadIfNeeded(
          this.getShellPorts(auth),
          getLastStartupCatalogSyncOutcome(),
        );
        this.router.navigate(postLoginTarget);
        return;
      }

      await this.finishAuthenticatedShell(auth, bootCorrelationId);
      await retryPostAuthCatalogDownloadIfNeeded(
        this.getShellPorts(auth),
        getLastStartupCatalogSyncOutcome(),
      );
    } catch (e) {
      telemetry.logError(e instanceof Error ? e.message : String(e), {
        source: 'bootstrap.afterLogin',
        correlationId: bootCorrelationId,
        error: e,
      });
      showFatalAppError({
        title: 'Error al iniciar la sesión o el layout.',
        hint: 'Revise la red, el estado de autenticación y la consola. Pulse Reintentar para recargar.',
        error: e,
      });
    }
  }

  private async runStartupSyncOverlay(bootCorrelationId: string): Promise<StartupCatalogSyncOutcome | null> {
    const telemetry = this.container.getTelemetry();
    telemetry.logInfo('Bootstrap: validando sincronización', { correlationId: bootCorrelationId });
    const outcome = await runStartupSyncOverlay(this.container.getCatalogDownload());
    noteStartupCatalogSyncOutcome(outcome);
    return outcome;
  }

  /**
   * After `DOMContentLoaded`: restore session, startup catalog GET, auth gates, layout, router, post-auth catalog retry.
   * Always ends the `app_boot` span in `finally`.
   */
  private async onDomContentLoaded(auth: ShellAuthApplicationPort): Promise<void> {
    const telemetry = this.container.getTelemetry();
    const bootCorrelationId = createCorrelationId();
    telemetry.logInfo('Bootstrap: inicio', { correlationId: bootCorrelationId });
    const bootTrace = telemetry.startTrace('app_boot', { correlationId: bootCorrelationId });
    const t0 = performance.now();
    let bootstrapOutcome = 'complete';
    try {
      await auth.initializeSession();
      const startupOutcome = await this.runStartupSyncOverlay(bootCorrelationId);
      const isAuth = await auth.isAuthenticated();
      const currentPath = globalThis.location.pathname;

      await syncTelemetryUserContext(auth, telemetry, isAuth);

      const shellPorts = this.getShellPorts(auth);

      if (gateUnauthenticatedDeepLink(this.presentation, shellPorts, isAuth, currentPath)) {
        bootstrapOutcome = 'redirect_login';
        return;
      }
      if (gateLoginPageOnly(this.presentation, shellPorts, isAuth, currentPath)) {
        bootstrapOutcome = 'login_shell_only';
        return;
      }

      await this.finishAuthenticatedShell(auth, bootCorrelationId);
      await retryPostAuthCatalogDownloadIfNeeded(shellPorts, startupOutcome);
    } catch (e) {
      bootstrapOutcome = 'error';
      telemetry.logError(e instanceof Error ? e.message : String(e), {
        source: 'bootstrap.dom',
        correlationId: bootCorrelationId,
        error: e,
      });
      showFatalAppError({
        title: 'Error al iniciar la sesión o el layout.',
        hint: 'Revise la red, el estado de autenticación y la consola. Pulse Reintentar para recargar.',
        error: e,
      });
      return;
    } finally {
      const elapsed = performance.now() - t0;
      telemetry.recordMetricHistogram('admin.frontend.bootstrap.duration_ms', elapsed, {
        outcome: bootstrapOutcome,
      });
      telemetry.recordMetricCounter('admin.frontend.bootstrap.runs_total', 1, { outcome: bootstrapOutcome });
      bootTrace.end();
    }
  }
}
